/**
 * 🎯 UNIFIED APPROVAL PIPELINE - Complete verification + business approval workflow
 * Consolidates /verification/overview + /approvals into single interface
 * Replaces both VerificationOverviewPage and ApprovalDashboardMUI
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Divider,
  Paper,
} from '@mui/material';
import {
  VerifiedUser,
  Schedule,
  CheckCircle,
  Warning,
  DirectionsCar,
  Person,
  Business,
  TrendingUp,
  MoreVert,
  Visibility,
  ThumbUp,
  ThumbDown,
  Comment,
  History,
  Star,
  LocationOn,
  Phone,
  Email,
  CalendarToday,
  Speed,
  Assignment,
} from '@mui/icons-material';

import {
  unifiedApprovalService,
  type UnifiedApprovalItem,
  type ApprovalStats,
  type ApprovalFilters
} from '@/services/unifiedApprovalService';
import { VerificationStatus } from '@/types';
import { logger } from '@/utils/logger';

const log = logger.module('UnifiedApprovalPipeline');

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const UnifiedApprovalPipeline: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [approvalItems, setApprovalItems] = useState<UnifiedApprovalItem[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UnifiedApprovalItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve_verification' | 'approve_business' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');

  const [filters, setFilters] = useState<ApprovalFilters>({
    stage: 'all',
    type: 'all',
    status: 'all'
  });

  useEffect(() => {
    loadApprovalData();
    loadApprovalStats();
  }, [filters]);

  const loadApprovalData = async () => {
    setIsLoading(true);
    try {
      const response = await unifiedApprovalService.getApprovalItems(filters);
      if (response.success && response.items) {
        setApprovalItems(response.items);
      }
    } catch (error) {
      log.error('Error loading approval data', { error });
    } finally {
      setIsLoading(false);
    }
  };

  const loadApprovalStats = async () => {
    try {
      const response = await unifiedApprovalService.getApprovalStats();
      if (response.success && response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error loading approval stats:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    
    // Update filters based on tab
    const stageMap = ['all', 'verification', 'business_approval'];
    setFilters(prev => ({ ...prev, stage: stageMap[newValue] as any }));
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, item: UnifiedApprovalItem) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
  };

  const handleAction = (type: 'approve_verification' | 'approve_business' | 'reject') => {
    setActionType(type);
    setActionDialogOpen(true);
    handleActionClose();
  };

  const executeAction = async () => {
    if (!selectedItem || !actionType) return;

    try {
      setIsLoading(true);
      let response;

      log.info('Executing approval action', { actionType, itemId: selectedItem.id });

      switch (actionType) {
        case 'approve_verification':
          response = await unifiedApprovalService.approveVerification(selectedItem.id, actionNotes);
          break;
        case 'approve_business':
          response = await unifiedApprovalService.approveBusinessApproval(selectedItem.id, actionNotes);
          break;
        case 'reject':
          const stage = selectedItem.currentStage;
          response = await unifiedApprovalService.rejectApproval(selectedItem.id, actionNotes, stage);
          break;
      }

      if (response?.success) {
        log.info('Action completed successfully, refreshing data', { actionType, itemId: selectedItem.id });
        await loadApprovalData();
        await loadApprovalStats();
        setActionDialogOpen(false);
        setActionNotes('');
      } else {
        log.warn('Action completed but not successful', { actionType, itemId: selectedItem.id, error: response?.error });
      }
    } catch (error) {
      log.error('Error executing action', { actionType, itemId: selectedItem?.id, error });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending_verification': 
      case 'pending_approval': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'verification': return <VerifiedUser />;
      case 'business_approval': return <Assignment />;
      case 'complete': return <CheckCircle />;
      default: return <Schedule />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'user_verification': return <Person />;
      case 'driver_activation': return <DirectionsCar />;
      case 'vehicle_listing': return <DirectionsCar />;
      case 'host_approval': return <Business />;
      default: return <Assignment />;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <VerifiedUser sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {stats.pendingVerification}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Verification
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Legal compliance review
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                {stats.pendingBusinessApproval}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approval
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Business readiness review
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {stats.approved}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved Today
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.dailyApprovals} daily average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {Math.round(stats.averageApprovalTime)}h
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Time
              </Typography>
              <Typography variant="caption" color="text.secondary">
                End-to-end approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderApprovalTable = (items: UnifiedApprovalItem[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User & Type</TableCell>
            <TableCell>Current Stage</TableCell>
            <TableCell>Verification Progress</TableCell>
            <TableCell>Business Readiness</TableCell>
            <TableCell>Urgency & Timeline</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={item.userInfo.profileImage}>
                    {item.userInfo.fullName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.userInfo.fullName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {getTypeIcon(item.type)}
                      <Typography variant="caption" color="text.secondary">
                        {item.type.replace('_', ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.userInfo.location}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {getStageIcon(item.currentStage)}
                  <Chip
                    label={item.currentStage.replace('_', ' ')}
                    color={item.currentStage === 'complete' ? 'success' : 'primary'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Chip
                  label={item.overallStatus.replace('_', ' ')}
                  color={getStatusColor(item.overallStatus) as any}
                  size="small"
                />
              </TableCell>

              <TableCell>
                <Box sx={{ minWidth: 120 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption">
                      {item.verification.completionPercentage}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Complete
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.verification.completionPercentage}
                    sx={{ height: 6, borderRadius: 3, mb: 1 }}
                    color={item.verification.completionPercentage === 100 ? 'success' : 'primary'}
                  />
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={`Identity: ${item.verification.identityStatus}`}>
                      <Chip
                        label="ID"
                        size="small"
                        color={item.verification.identityStatus === VerificationStatus.APPROVED ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    </Tooltip>
                    <Tooltip title={`Documents: ${item.verification.documentsStatus}`}>
                      <Chip
                        label="DOC"
                        size="small"
                        color={item.verification.documentsStatus === VerificationStatus.APPROVED ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    </Tooltip>
                    <Tooltip title={`Background: ${item.verification.backgroundStatus}`}>
                      <Chip
                        label="BG"
                        size="small"
                        color={item.verification.backgroundStatus === VerificationStatus.APPROVED ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    </Tooltip>
                  </Stack>
                </Box>
              </TableCell>

              <TableCell>
                {item.businessApproval ? (
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" display="block">
                      Profile: {item.businessApproval.profileCompleteness}%
                    </Typography>
                    <Typography variant="caption" display="block">
                      Service: {item.businessApproval.serviceReadiness}%
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Star sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption">
                        {item.businessApproval.qualityScore}/100
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Awaiting verification
                  </Typography>
                )}
              </TableCell>

              <TableCell>
                <Chip
                  label={item.urgencyLevel}
                  color={getUrgencyColor(item.urgencyLevel) as any}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.daysInQueue} days in queue
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.submittedAt).toLocaleDateString()}
                </Typography>
              </TableCell>

              <TableCell>
                <IconButton
                  onClick={(e) => handleActionClick(e, item)}
                  size="small"
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const filteredItems = approvalItems.filter(item => {
    if (currentTab === 1 && item.currentStage !== 'verification') return false;
    if (currentTab === 2 && item.currentStage !== 'business_approval') return false;
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>🎯 Unified Approval Pipeline:</strong> This replaces both /verification/overview and /approvals 
          with a single, comprehensive workflow. Legal compliance verification flows seamlessly into business approval.
        </Typography>
      </Alert>

      {renderStatsCards()}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab 
              label={`All Items (${approvalItems.length})`}
              icon={<Assignment />} 
              iconPosition="start"
            />
            <Tab 
              label={`Verification Stage (${approvalItems.filter(i => i.currentStage === 'verification').length})`}
              icon={<VerifiedUser />} 
              iconPosition="start"
            />
            <Tab 
              label={`Business Approval (${approvalItems.filter(i => i.currentStage === 'business_approval').length})`}
              icon={<CheckCircle />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Complete Approval Pipeline
            </Typography>
            {renderApprovalTable(filteredItems)}
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Legal Compliance Verification
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Review identity, documents, and background checks for legal compliance.
            </Alert>
            {renderApprovalTable(filteredItems)}
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Business Readiness Approval
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Review service quality, profile completeness, and platform readiness.
            </Alert>
            {renderApprovalTable(filteredItems)}
          </Box>
        </TabPanel>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => setDetailsOpen(true)}>
          <ListItemIcon><Visibility /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <Divider />
        {selectedItem?.currentStage === 'verification' && (
          <MenuItem onClick={() => handleAction('approve_verification')}>
            <ListItemIcon><CheckCircle sx={{ color: 'success.main' }} /></ListItemIcon>
            <ListItemText>Approve Verification</ListItemText>
          </MenuItem>
        )}
        {selectedItem?.currentStage === 'business_approval' && (
          <MenuItem onClick={() => handleAction('approve_business')}>
            <ListItemIcon><ThumbUp sx={{ color: 'success.main' }} /></ListItemIcon>
            <ListItemText>Approve for Platform</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction('reject')}>
          <ListItemIcon><ThumbDown sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Reject</ListItemText>
        </MenuItem>
      </Menu>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve_verification' && 'Approve Verification'}
          {actionType === 'approve_business' && 'Approve for Platform'}
          {actionType === 'reject' && 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={executeAction} 
            variant="contained"
            color={actionType === 'reject' ? 'error' : 'success'}
            disabled={isLoading}
          >
            {actionType === 'reject' ? 'Reject' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};